'use client';

import { type HTMLAttributes, useEffect } from 'react';
import styled from 'styled-components';
import { getGptSlot, installBanners, refreshServedSlot, whenBiffLoaded } from '@/app/common/biff/biffLoader.ts';
import { PAGE_TYPE } from '@/app/common/pageType';

export const ROOMS_AD_ID = 'div-gpt-ad-intrip1';

/**
 * Shows the rooms ad in the node currently on the page.
 *
 * biff defines the slot while it loads the page configuration (only when that configuration lists the rooms
 * ad) and `installBanners` displays only ids that already have a slot, so the slot must never be destroyed
 * here: nothing would define it again. While the slot is not there yet — gpt still initialising, or a
 * configuration without the rooms ad — biff's queued install takes care of the first display. Once it is
 * there, googletag needs an explicit display (a no-op after the first) plus a refresh: the ad request is
 * what fills the node, and after a rooms reload the node is a fresh element the earlier render never saw.
 */
export function showRoomsAd() {
  const slot = getGptSlot(ROOMS_AD_ID);
  if (!slot) {
    installBanners({ elements: [ROOMS_AD_ID], provider: 'googleDFP' });
    return;
  }

  window.googletag?.display?.(ROOMS_AD_ID);
  refreshServedSlot(slot);
}

const RoomsAdSlot = styled.div`
  display: flex;
  justify-content: center;
  /* An oversized creative is clipped rather than allowed to widen the rooms column. */
  max-width: 100%;
  overflow: hidden;

  /* Nothing to show means nothing to take room: the slot is empty until GPT fills it, and an
     unfilled one keeps either an empty container or a zero-height frame. Hiding it outright
     also drops the row gap the rooms list would otherwise keep for it, so no blank band is
     left behind. Same shapes the result page's intra-list slot looks for. */
  &:empty,
  &:has(> div:empty),
  &:has(> div > [style*='height:0']),
  &:has(> div > [style*='height: 0']) {
    display: none;
  }
`;

/**
 * MPH-1089 / cux_4084: the ad the evo detail page shows right after the first room option.
 *
 * It installs itself instead of being listed in `HOTEL_DETAIL_POSITIONS`, because unlike the page's
 * other slots it is not in the DOM when the detail page registers its banners — the rooms arrive from
 * a later request. Installing it up front would define a slot for an element that is not there yet
 * and never render anything into it.
 */
export function RoomsAd(props: HTMLAttributes<HTMLDivElement>) {
  useEffect(() => {
    let cancelled = false;
    // Waits for the detail page's load(): the slot only exists once biff has processed that configuration.
    // Keyed so a results-page load() still finishing after a client-side navigation cannot release it early.
    whenBiffLoaded(PAGE_TYPE.HOTEL_DETAIL).then(() => {
      if (cancelled) return;
      // Not a one-shot install: a rooms reload — a date change, a transport change, a similar-hotel
      // navigation — drops this component and mounts it again over a fresh, empty element, so every
      // mount has to bring the ad into the node currently on the page (see showRoomsAd).
      showRoomsAd();
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return <RoomsAdSlot id={ROOMS_AD_ID} {...props} />;
}
